import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Typography } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import HorizontalDivider from 'components/HorizontalDivider';
import BullhornIcon from 'components/Icon';
import { stripHtmlPlainText } from 'helpers/stripHtmlPlainText';

import { DASHBOARD_CARD_STYLE } from '../../constants';
import * as DashboardActions from '../../store/actions';
import SectionHeader from './SectionHeader';
import RecentPatientActivitySection from './RecentPatientActivitySection';
import AnnouncementModal from './AnnouncementModal';

const HELP_CENTRE_URL = 'https://mypaknghelpcentre.refined.site/space/NG/';

const ANNOUNCEMENT_PREVIEW_MAX_LENGTH = 140;

const getAnnouncementPreview = (title, content) => {
  let text = stripHtmlPlainText(content);
  if (!text) {
    return { preview: '', isTruncated: false };
  }

  if (title) {
    const normalizedTitle = title.trim().toLowerCase();
    if (text.toLowerCase().startsWith(normalizedTitle)) {
      text = text.slice(title.trim().length).replace(/^[\s,:-]+/, '');
    }
  }

  if (text.length > ANNOUNCEMENT_PREVIEW_MAX_LENGTH) {
    return {
      preview: `${text.slice(0, ANNOUNCEMENT_PREVIEW_MAX_LENGTH).trim()}...`,
      isTruncated: true,
    };
  }

  return { preview: text, isTruncated: false };
};

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    height: '100%',
    padding: 0,
  },
  shadow: {
    width: '100%',
    flex: 1,
    minHeight: 0,
    height: '100%',
    ...DASHBOARD_CARD_STYLE,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  contentArea: {
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    borderRadius: 4,
    gap: theme.spacing(1.5),
  },
  noticeSection: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  announcementItem: {
    border: '1px solid #DDE9EF',
    borderRadius: 10,
    padding: '12px',
    background: '#EAF7FD',
    display: 'flex',
    alignItems: 'flex-start',
  },
  iconWrap: {
    flexShrink: 0,
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  announcementBody: {
    flex: 1,
    minWidth: 0,
  },
  announcementTitle: {
    fontWeight: 600,
    color: '#800000',
    marginBottom: 4,
  },
  announcementContent: {
    color: '#486470',
    fontSize: 13,
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  learnMore: {
    textTransform: 'none',
    marginTop: 10,
    padding: '4px 14px',
    border: '1px solid #0B9FD9',
    borderRadius: 16,
    color: '#0B9FD9',
    backgroundColor: '#FFFFFF',
    fontSize: 13,
    fontWeight: 500,
    '&:hover': {
      border: '1px solid #0B9FD9',
      backgroundColor: '#EAF7FD',
    },
  },
  helpSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    gap: theme.spacing(1),
  },
  scrollableList: {
    overflowY: 'auto',
    minHeight: 0,
    scrollbarWidth: 'thin',
    scrollbarColor: '#BFE3F3 transparent',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#BFE3F3',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#0B9FD9',
    },
  },
  helpList: {
    flex: 1,
    minHeight: 0,
    paddingBottom: 8,
  },
  helpItem: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '12px 0',
    borderBottom: '1px solid #EEF4F8',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:first-child': {
      paddingTop: 0,
    },
  },
  helpIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: '#EAF7FD',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    flexShrink: 0,
  },
  helpBody: {
    flex: 1,
    minWidth: 0,
  },
  helpTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  helpTitle: {
    fontWeight: 600,
    color: '#18323F',
    fontSize: 14,
    lineHeight: 1.35,
  },
  helpDate: {
    color: '#9BB0BB',
    fontSize: 11,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    marginTop: 2,
  },
  readMoreLink: {
    fontSize: 13,
    color: '#0B9FD9',
    cursor: 'pointer',
    marginTop: 4,
    padding: 0,
    minWidth: 0,
    textTransform: 'none',
    justifyContent: 'flex-start',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: 'transparent',
    },
  },
  emptyState: {
    flex: 1,
    minHeight: 0,
    padding: '4px 4px 8px',
  },
  emptyText: {
    color: '#9BB0BB',
    fontSize: 13,
  },
}));

function NoticeBoard() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
  const { data: notices, loading: noticesLoading } = useSelector((state) => state.dashboard.notice);
  const { data: announcement, loading: announcementLoading } = useSelector((state) => state.dashboard.announcement);

  useEffect(() => {
    dispatch(DashboardActions.getNotices());
    dispatch(DashboardActions.getActiveAnnouncement());
  }, [dispatch]);

  const { preview: announcementPreview, isTruncated: isAnnouncementTruncated } = announcement?.content
    ? getAnnouncementPreview(announcement.title, announcement.content)
    : { preview: '', isTruncated: false };

  const openUrl = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={classes.root}>
      <div className={clsx(classes.shadow)}>
        <div className={clsx(classes.contentArea)}>
          <div className={classes.noticeSection}>
            <SectionHeader title={t('dashboard:NOTICE_TITLE')} />
            {announcementLoading ? (
              <div className={classes.announcementItem}>
                <Skeleton variant="circle" width={48} height={48} style={{ marginRight: 12, flexShrink: 0 }} />
                <div className={classes.announcementBody}>
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="rect" width={100} height={30} style={{ borderRadius: 16, marginTop: 10 }} />
                </div>
              </div>
            ) : (
              announcement && (
                <div className={classes.announcementItem}>
                  <div className={classes.iconWrap}>
                    <BullhornIcon icon="bullhorn" color="#0B9FD9" size={1.35} width={1.3} />
                  </div>
                  <div className={classes.announcementBody}>
                    <Typography variant="subtitle2" className={clsx(classes.announcementTitle, 'cursor-default')}>
                      {announcement.title}
                    </Typography>
                    {announcementPreview && (
                      <Typography variant="body2" className={clsx(classes.announcementContent, 'cursor-default')}>
                        {announcementPreview}
                      </Typography>
                    )}
                    {isAnnouncementTruncated && (
                      <Button
                        size="small"
                        className={clsx(classes.learnMore)}
                        onClick={() => setIsAnnouncementModalOpen(true)}
                      >
                        {t('dashboard:ANNOUNCEMENT_READ_MORE')}
                      </Button>
                    )}
                  </div>
                </div>
              )
            )}
          </div>

          <HorizontalDivider />

          <div className={classes.helpSection}>
            <SectionHeader
              title={t('dashboard:HELP_CENTRE_TITLE')}
              actionLabel={t('dashboard:HELP_CENTRE_VIEW_ALL')}
              actionHref={HELP_CENTRE_URL}
            />
            {noticesLoading ? (
              <div className={clsx(classes.helpList, classes.scrollableList)}>
                {[1, 2, 3].map((i) => (
                  <div key={i} className={classes.helpItem}>
                    <Skeleton variant="rect" width={36} height={36} style={{ borderRadius: 8, marginRight: 12 }} />
                    <div className={classes.helpBody}>
                      <Skeleton variant="text" width="70%" height={18} />
                      <Skeleton variant="text" width={80} height={16} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (notices || []).length > 0 ? (
              <div className={clsx(classes.helpList, classes.scrollableList)}>
                {(notices || []).map((notice) => (
                  <div key={notice.id} className={classes.helpItem}>
                    <div className={classes.helpIcon}>
                      <BullhornIcon icon="file-alt" color="#0B9FD9" size={1} width={0.9} />
                    </div>
                    <div className={classes.helpBody}>
                      <div className={classes.helpTitleRow}>
                        <Typography className={clsx(classes.helpTitle, 'cursor-default')}>{notice.title}</Typography>
                        {notice.date && (
                          <Typography className={clsx(classes.helpDate, 'cursor-default')} component="span">
                            {notice.date}
                          </Typography>
                        )}
                      </div>
                      <Button
                        className={classes.readMoreLink}
                        disabled={!notice.url}
                        onClick={() => openUrl(notice.url)}
                      >
                        {t('dashboard:NOTICE_READ_MORE')} &rarr;
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={classes.emptyState}>
                <Typography className={clsx(classes.emptyText, 'cursor-default')}>
                  {t('dashboard:NOTICE_EMPTY')}
                </Typography>
              </div>
            )}
          </div>

          <HorizontalDivider />

          <RecentPatientActivitySection />
        </div>
      </div>
      <AnnouncementModal
        open={isAnnouncementModalOpen}
        title={announcement?.title}
        content={announcement?.content}
        onClose={() => setIsAnnouncementModalOpen(false)}
      />
    </div>
  );
}

export default React.memo(NoticeBoard);
